// Special abilities: ways to travel far, attacks and sleep styles that only some companions have
// (the Warden digs, the Shulker teleports, the Ghast flies). Each one is a file
// engine/abilities/<name>.js whose default export has a label and the parts it offers:
//
//   export default {
//     label: 'Digs through the ground',
//     travel: async (spot, g, from) => { ... },             // a way to get somewhere far
//     down: true,                                           // ... that only goes down (a chicken fluttering)
//     attack: async (g, mite) => { ... },                   // how it fights a pest (a bug, glitch or endermite: pests.js)
//     range: 'far',                                         // ... from afar (arrows, fireballs, beams): in a duel it keeps its distance (fights.js)
//     sleep: { fallAsleep: async g => { ... }, wakeUp: async g => { ... } },
//     idle: async g => { ... },                             // something it does now and then on its own
//     hop: async (target, g, toScale) => { ... },           // its own way of doing every small jump
//     walk: async (ledge, toX, g, { stayTurned }) => { ... },   // its own way of walking along a surface (a penguin's waddle)
//     moves: [{ id, move, duel, reach, power }],            // an attack made of several moves: how a duel picks them (fights.js)
//     ultra: async (g, targets, strike) => { ... },         // a duel's last resort, once ("ultra" in its design's "fight": fights.js)
//     fall: async (g, from) => { ... },                     // its own way of going down when it's beaten ("fall" in its "fight": Pac-Man deflates)
//     pointer: { play: async (g, { shown }) => { ... },     // its own way to play with the pointer ("pointer" in its moves: pointer.js)
//                reach: 'screen', again: [lo, hi] },        // ... from anywhere on screen (else only when it lingers close), seconds between plays
//   };
//
// A design names the ones it uses in "moves" ("travel": ["dig"], "attack": "sonic", "sleep":
// "dig", "idle": ["graze"], "hop": "bounce", "walk": "waddle") and its ultra and fall in "fight". loadAbilities loads just those, so a new ability is one new file
// plus its name in the designs that use it. The engine's own styles (BUILT_IN, WALKS) need no file.

export const BUILT_IN = new Set(['sword', 'flee', 'bed', 'sit', 'curl', 'lie', 'float']);
const WALKS = new Set(['walk', 'profile', 'hop', 'fly', 'roll', 'none']);   // the engine's own walk styles
const loaded = new Map();

// The abilities a design names that aren't built into the engine.
export function abilityNames(design) {
  const m = design.moves || {};
  return [...(m.travel || []), m.attack, m.sleep, ...(m.idle || []), m.hop, WALKS.has(m.walk) ? null : m.walk, m.pointer, design.fight?.ultra, design.fight?.fall].filter(name => name && !BUILT_IN.has(name));
}

export async function loadAbilities(designs) {
  registerDesigns(designs);
  const names = new Set(Object.values(designs).flatMap(abilityNames));
  await Promise.all([...names].filter(name => !loaded.has(name)).map(async name => {
    const mod = await import(new URL(`./abilities/${name}.js`, import.meta.url));
    loaded.set(name, mod.default);
  }));
  // The other designs these abilities asked for (designFor), unless they're here already.
  await Promise.all([...wanted].filter(id => !known.has(id)).map(async id => {
    try {
      const res = await fetch(new URL(`../companions/${id}/design.json`, import.meta.url));
      known.set(id, res.ok ? await res.json() : null);
    } catch { known.set(id, null); }
  }));
}

// Another companion's design that an ability draws (Hades calling up Cerberus, who may not be on
// screen). Its module asks when it loads, `const cerberus = designFor('cerberus')`, and calls
// cerberus() when it needs it: the design, or null if it couldn't be had. loadAbilities loads them
// with the abilities, before anything runs (so a summons takes the same time every time: the gallery
// depends on it). A bundle registers its designs up front (registerDesigns): it can't fetch them,
// nor wait at a module's top level.
const known = new Map(), wanted = new Set();
export function designFor(id) {
  wanted.add(id);
  return () => known.get(id) ?? null;
}
export function registerDesigns(designs) {
  for (const [id, design] of Object.entries(designs)) known.set(id, design);
}

// A bundle registers the ability modules it already imported (their default exports, by name),
// so loadAbilities has nothing left to import.
export function registerAbilities(abilities) {
  for (const [name, ab] of Object.entries(abilities)) loaded.set(name, ab);
}

export const ability = name => loaded.get(name) || null;
